import React from "react";
import {Check, Circle} from "lucide-react";

import {cn} from "@/lib/utils";
import type {RequirementItem} from "@/main/validation/requirements";

type Props = {
    items: RequirementItem[];
    visible?: boolean;
    highlight?: boolean;
    title?: React.ReactNode;
    className?: string;
    listId?: string;
    variant?: "panel" | "plain";
    /**
     * Whether this list owns the `aria-live` region announcing its own content. Defaults to `true`,
     * which is correct wherever the list is rendered in flow and its wrapper therefore pre-exists the
     * change (`variant="panel"`, and the mobile composer bar's inline block).
     *
     * `RequirementsPopover` passes `false`: its content mounts WITH the popover, so a region declared
     * here would be inserted already-populated, which screen readers are unreliable about
     * (`CREATE-PUBLISH-43`). That consumer keeps a persistent region of its own instead, outside the
     * popover, and two regions holding the same text would announce it twice.
     */
    announce?: boolean;
};

export default function RequirementsList({
    items,
    visible = false,
    highlight = false,
    title,
    className,
    listId,
    variant = "panel",
    announce = true,
}: Props) {
    const transitionDurationMs = 500;
    const [isPresent, setIsPresent] = React.useState(visible);

    React.useEffect(() => {
        if (visible) {
            setIsPresent(true);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setIsPresent(false);
        }, transitionDurationMs);

        return () => window.clearTimeout(timeoutId);
    }, [transitionDurationMs, visible]);

    if (items.length === 0 || !isPresent) {
        return null;
    }

    const wrapperClassName = cn(
        "overflow-hidden will-change-[max-height,opacity,transform] transition-[max-height,opacity,margin,transform] duration-500 ease-in-out motion-reduce:transition-none",
        variant === "panel"
            ? visible
                ? "mt-3 max-h-80 opacity-100 translate-y-0"
                : "mt-0 max-h-0 opacity-0 -translate-y-1"
            : visible
                ? "mt-2 max-h-80 opacity-100 translate-y-0"
                : "mt-0 max-h-0 opacity-0 -translate-y-1",
        className
    );

    const content = (
        <>
            {title && <p className="text-sm font-medium text-foreground">{title}</p>}
            <ul
                id={listId}
                className={cn(title ? "mt-2" : undefined, "space-y-1.5")}
            >
                {items.map((item) => {
                    const Icon = item.met ? Check : Circle;
                    return (
                        <li
                            key={item.id}
                            className={cn(
                                "flex items-start gap-2 text-sm transition-colors duration-500",
                                item.met
                                    ? "text-[var(--success-text)]"
                                    : highlight
                                        // `--error-text`, not `--destructive` (CREATE-PUBLISH-30). The
                                        // destructive token is sized for a solid button fill; as body
                                        // text on the popover surface it measured 3.53:1 in dark, below
                                        // the floor. `--error-text` is the same red mixed toward
                                        // `--text-main`, and it is the paired token for exactly this
                                        // use. Defined in both themes (css/00-variables.css:51, :344).
                                        ? "text-[var(--error-text)]"
                                        : "text-muted-foreground"
                            )}
                        >
                            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{item.text}</span>
                        </li>
                    );
                })}
            </ul>
        </>
    );

    return (
        <div aria-live={announce ? "polite" : undefined} className={wrapperClassName}>
            {variant === "panel" ? (
                <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-3">
                    {content}
                </div>
            ) : (
                content
            )}
        </div>
    );
}
